import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Archive, BookCheck, Eye, Pencil, Plus, Send, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PermissionButton } from '@/client/components/admin/PermissionButton';
import { PERMISSIONS } from '@/client/config/permissions';
import { useGate } from '@/client/hooks/useGate';
import { usePoliciesQuery, usePolicyAcksQuery, usePolicyActionMutation, useUpsertPolicyMutation } from '@/client/api/hr/records';
import { HR_POLICY_CATEGORY_LABEL, type HrPolicy, type HrPolicyCategory } from '@/client/types/hr';
import { HrPageShell } from './components/HrPageShell';
import { DeleteConfirmDialog, EmptyState, ProgressBar, RowMenu, StatusBadge } from './components/hr-ui';
import { formatDay } from './components/leave-format';
import { useEmployees } from './hr-store';

const inputClass = 'h-9 w-full rounded-md border border-gray-200 bg-white px-3 text-sm outline-none ring-blue-500 focus:ring-2';
const STATUS_BADGE = { draft: 'bg-gray-100 text-gray-700', published: 'bg-emerald-50 text-emerald-700', archived: 'bg-gray-100 text-gray-400' };

/** Company policies. Publishing bumps the version; an acknowledgement is for
 *  one version, so a changed policy is acknowledged again in My workplace. */
export default function PoliciesPage() {
  const policiesQ = usePoliciesQuery();
  const employees = useEmployees();
  const upsert = useUpsertPolicyMutation();
  const act = usePolicyActionMutation();
  const canManage = useGate(PERMISSIONS.hr.manage).allowed;
  const [editing, setEditing] = useState<Partial<HrPolicy> | null>(null);
  const [reading, setReading] = useState<HrPolicy | null>(null);
  const [acksFor, setAcksFor] = useState<HrPolicy | null>(null);
  const [pending, setPending] = useState<{ policy: HrPolicy; action: 'publish' | 'archive' } | null>(null);
  const acksQ = usePolicyAcksQuery(acksFor?.id);
  const byId = useMemo(() => new Map(employees.map((e) => [e.id, e])), [employees]);

  const policies = policiesQ.data ?? [];
  const published = policies.filter((p) => p.status === 'published');
  const outstanding = published.filter((p) => p.requiresAck).reduce((n, p) => n + Math.max((p.toAcknowledge ?? 0) - (p.acknowledged ?? 0), 0), 0);

  const save = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    upsert.mutate(editing, {
      onSuccess: () => {
        toast.success(editing.id ? 'Saved. Publish to make the change live.' : 'Draft saved');
        setEditing(null);
      },
      onError: (err) => toast.error((err as Error).message || 'Could not save'),
    });
  };

  return (
    <HrPageShell
      mock={false}
      title="Policies"
      description="Company policies everyone works under. Publishing makes a policy live in My workplace; publishing a change asks everyone to acknowledge it again."
      crumbs={[{ label: 'Human Resource', href: '/admin/human-resource' }, { label: 'Documents' }, { label: 'Policies' }]}
      icon={BookCheck}
      actions={
        <PermissionButton code={PERMISSIONS.hr.manage} className="gap-2" onClick={() => setEditing({ title: '', category: 'other', body: '', requiresAck: true })}>
          <Plus className="h-4 w-4" /> New policy
        </PermissionButton>
      }
      stats={[
        { key: 'published', label: 'Published', value: published.length },
        { key: 'drafts', label: 'Drafts', value: policies.filter((p) => p.status === 'draft').length },
        { key: 'outstanding', label: 'Acknowledgements outstanding', value: outstanding },
      ]}
    >
      {policiesQ.isError ? (
        <EmptyState label={`Could not load policies: ${(policiesQ.error as Error)?.message ?? 'unknown error'}`} />
      ) : policiesQ.isLoading ? (
        <EmptyState label="Loading policies…" />
      ) : policies.length === 0 ? (
        <EmptyState label="No policies yet. Write the first one." />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-100 bg-white shadow-sm">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-3 font-medium">Policy</th>
                <th className="px-4 py-3 font-medium">Category</th>
                <th className="px-4 py-3 font-medium">Version</th>
                <th className="px-4 py-3 font-medium">Acknowledged</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {policies.map((p) => {
                const pct = p.toAcknowledge ? ((p.acknowledged ?? 0) / p.toAcknowledge) * 100 : 0;
                return (
                  <tr key={p.id} className="cursor-pointer hover:bg-gray-50" onClick={() => setReading(p)}>
                    <td className="px-4 py-3 font-medium text-gray-900">{p.title}</td>
                    <td className="px-4 py-3 text-gray-600">{HR_POLICY_CATEGORY_LABEL[p.category]}</td>
                    <td className="px-4 py-3 text-gray-600">{p.version ? `v${p.version}` : '—'}</td>
                    <td className="px-4 py-3">
                      {p.status === 'published' && p.requiresAck ? (
                        <>
                          <p className="mb-1 text-xs text-gray-500">
                            {p.acknowledged ?? 0} / {p.toAcknowledge ?? 0}
                          </p>
                          <ProgressBar value={pct} className="w-24" />
                        </>
                      ) : (
                        <span className="text-xs text-gray-400">{p.requiresAck ? '—' : 'not required'}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge label={p.status} className={`capitalize ${STATUS_BADGE[p.status]}`} />
                      {p.publishedAt && <p className="text-[11px] text-gray-400">{formatDay(p.publishedAt)}</p>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <RowMenu
                        items={[
                          { label: 'Read', icon: Eye, onSelect: () => setReading(p) },
                          { label: 'Edit', icon: Pencil, hidden: !canManage || p.status === 'archived', onSelect: () => setEditing(p) },
                          { label: p.status === 'published' ? 'Publish changes' : 'Publish', icon: Send, hidden: !canManage || p.status === 'archived', onSelect: () => setPending({ policy: p, action: 'publish' }) },
                          { label: 'Who has acknowledged', icon: Users, hidden: p.status !== 'published' || !p.requiresAck, onSelect: () => setAcksFor(p) },
                          { label: 'Archive', icon: Archive, danger: true, hidden: !canManage || p.status === 'archived', onSelect: () => setPending({ policy: p, action: 'archive' }) },
                        ]}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing?.id ? 'Edit policy' : 'New policy'}</DialogTitle>
          </DialogHeader>
          {editing && (
            <form onSubmit={save} className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="block space-y-1.5">
                  <span className="text-xs font-medium text-gray-600">Title</span>
                  <input required value={editing.title ?? ''} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className={inputClass} />
                </label>
                <label className="block space-y-1.5">
                  <span className="text-xs font-medium text-gray-600">Category</span>
                  <select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value as HrPolicyCategory })} className={inputClass}>
                    {Object.entries(HR_POLICY_CATEGORY_LABEL).map(([v, l]) => (
                      <option key={v} value={v}>
                        {l}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <label className="block space-y-1.5">
                <span className="text-xs font-medium text-gray-600">Policy text</span>
                <textarea
                  rows={14}
                  value={editing.body ?? ''}
                  onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                  className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm outline-none ring-blue-500 focus:ring-2"
                />
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={editing.requiresAck ?? true} onChange={(e) => setEditing({ ...editing, requiresAck: e.target.checked })} />
                Everyone must acknowledge it
              </label>
              <DialogFooter className="gap-2 sm:gap-0">
                <Button type="button" variant="outline" onClick={() => setEditing(null)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={upsert.isPending}>
                  Save
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!reading} onOpenChange={(o) => !o && setReading(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {reading?.title} {reading?.version ? <span className="text-sm font-normal text-gray-400">v{reading.version}</span> : null}
            </DialogTitle>
          </DialogHeader>
          <p className="whitespace-pre-wrap text-sm text-gray-800">{reading?.body || 'Nothing written yet.'}</p>
        </DialogContent>
      </Dialog>

      <Dialog open={!!acksFor} onOpenChange={(o) => !o && setAcksFor(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {acksFor?.title} · v{acksQ.data?.version ?? acksFor?.version}
            </DialogTitle>
          </DialogHeader>
          {acksQ.isLoading || !acksQ.data ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-400">Not yet ({acksQ.data.waiting.length})</p>
                <ul className="space-y-1 text-sm text-gray-800">
                  {acksQ.data.waiting.map((id) => (
                    <li key={id}>{byId.get(id)?.name ?? id}</li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-400">Acknowledged ({acksQ.data.acknowledged.length})</p>
                <ul className="space-y-1 text-sm text-gray-800">
                  {acksQ.data.acknowledged.map((a) => (
                    <li key={a.employeeId}>
                      {byId.get(a.employeeId)?.name ?? a.employeeId} <span className="text-xs text-gray-400">{formatDay(a.at)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog
        open={!!pending}
        onOpenChange={(o) => !o && setPending(null)}
        title={pending?.action === 'archive' ? `Archive ${pending.policy.title}?` : `Publish ${pending?.policy.title}?`}
        description={
          pending?.action === 'archive'
            ? 'It disappears from My workplace. Acknowledgements are kept.'
            : pending?.policy.status === 'published'
              ? `This becomes v${(pending.policy.version ?? 0) + 1}.${pending.policy.requiresAck ? ' Everyone is asked to acknowledge it again.' : ''}`
              : `Everyone sees it in My workplace${pending?.policy.requiresAck ? ' and is asked to acknowledge it' : ''}.`
        }
        actionLabel={pending?.action === 'archive' ? 'Archive' : 'Publish'}
        danger={pending?.action === 'archive'}
        onConfirm={() => {
          if (pending)
            act.mutate(
              { id: pending.policy.id, action: pending.action },
              { onSuccess: () => toast.success(pending.action === 'archive' ? 'Archived' : 'Published'), onError: (e) => toast.error((e as Error).message) },
            );
          setPending(null);
        }}
      />
    </HrPageShell>
  );
}
